import React from 'react';
import { Button, CircularProgress, Box, Typography } from '@material-ui/core';
import ReactPlayer from 'react-player';
import { useExperience } from '../contexts/experience';
import { useTheme } from '../contexts/theme';
import String from './String';
import { useLanguage } from '../contexts/language';

const BOX_SHADOW =
  'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.3) 2px 2px 2px 0px';

const SingleVideoPlayer = ({ video, videoSeeksMapRef, compact = false }) => {
  const { geoXpVideoEvents } = useExperience();

  const playerInstance = React.useRef(null);
  const [playerReady, setPlayerReady] = React.useState(false);
  const [playerStarted, setPlayerStarted] = React.useState(false);

  const { content } = useLanguage();

  const { styles } = useTheme();
  const backgroundColor = styles.palette.background.default;

  const previousSeek = React.useMemo(() => videoSeeksMapRef?.current?.get(video.id), []);

  const handleStart = React.useCallback(() => {
    if (playerStarted) return;

    if (previousSeek && previousSeek !== 0 && playerInstance.current) {
      playerInstance.current.seekTo(previousSeek, 'seconds');
    }

    setPlayerStarted(true);
  }, [playerStarted, previousSeek]);

  const handleEnd = () => {
    setPlayerStarted(false);
    videoSeeksMapRef?.current?.set(video?.id, 0);
    geoXpVideoEvents.videoEnded(video.spotId);
  };

  const isGeo = React.useMemo(() => !!video?.position, [video]);

  return (
    <Box role="region" aria-labelledby={`video-title-${video.id}`}>
      {!compact && (
        <Typography
          id={`video-title-${video.id}`}
          color="textSecondary"
          variant="h5"
          fontWeight="bold"
          marginBottom={1}
        >
          {video?.label}
        </Typography>
      )}

      <Box
        width="100%"
        mb={compact ? 0 : 2}
        borderRadius={compact ? 0 : '0.5rem'}
        overflow="hidden"
        boxShadow={compact ? 'none' : BOX_SHADOW}
        lineHeight={0}
      >
        {!playerReady && (
          <Box
            position="absolute"
            width="100%"
            height="100%"
            py={8}
            display="flex"
            justifyContent="center"
            alignItems="center"
            backgroundColor={backgroundColor}
            aria-busy="true"
            aria-live="polite"
          >
            <CircularProgress color="secondary" size="25px" />
          </Box>
        )}
        <ReactPlayer
          playing={playerStarted}
          ref={playerInstance}
          width="100%"
          height="100%"
          controls
          playsinline
          config={{
            file: {
              attributes: {
                // native controls overflow menu (Chromium): drop Download and the
                // browser's own Picture-in-Picture (we ship our own PIP), keep
                // only Playback speed. UI deterrence, not content protection.
                controlsList: 'nodownload',
                disablePictureInPicture: true,
              },
            },
          }}
          url={video.url}
          title={video.label} // utile per screen reader su alcuni screen reader
          aria-label={`Video player: ${video.label}`}
          onReady={() => {
            setPlayerReady(true);
            geoXpVideoEvents.videoReady(video.spotId);
          }}
          onStart={() => {
            setPlayerStarted(true);
            geoXpVideoEvents.videoStarted(video.spotId);
          }}
          onProgress={(data) => videoSeeksMapRef?.current?.set(video?.id, data.playedSeconds)}
          onPlay={() => setPlayerStarted(true)}
          onPause={() => setPlayerStarted(false)}
          onEnded={handleEnd}
        />
      </Box>

      {!playerStarted && (
        <Box position="absolute" width="100%" display="flex" justifyContent="center" alignItems="center">
          <Button
            color="primary"
            variant="contained"
            onClick={handleStart}
            disabled={!playerReady}
            aria-label={previousSeek ? `${content('resume')} video` : `${content('start')} video`}
          >
            {previousSeek ? <String s="resume" /> : <String s="start" />}
          </Button>
        </Box>
      )}

      {playerStarted && !isGeo && (
        <Box mt={2}>
          <Button
            color="secondary"
            variant="outlined"
            onClick={handleEnd}
            data-testid="agami-video-stop-btn"
            aria-label={content('stop_video')}
          >
            <String s="stop" />
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SingleVideoPlayer;
